import {
  getRetailItemTypeLabel,
  getRetailItemTypeValue,
  getRetailItemTypes,
} from "../../constants/RetailItemTypes";
import { ProductEnum } from "../../constants/login";
import { RetailItemExcelSourceField } from "../enums/retailItemExcel";
import { isAllowedAutocompleteValue } from "./roomDefinitionImportValidation";

export type RetailItemImportValidationResult = {
  valid: boolean;
  message?: string;
  rowErrors: string[];
};

const normalizeKey = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const isAllowedItemType = (
  value: unknown,
  productType?: ProductEnum | string | null,
): boolean => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return false;
  }
  if (getRetailItemTypeLabel(raw, productType)) {
    return true;
  }
  if (getRetailItemTypeValue(raw, productType) != null) {
    return true;
  }
  return getRetailItemTypes(productType).some(
    (option) =>
      option.label.toLowerCase() === raw.toLowerCase() ||
      String(option.value) === raw,
  );
};

/** Ensures imported retail items use outlets/categories/item types defined in onboarding. */
export const validateRetailItemImportAgainstMasterData = (
  rows: Record<string, unknown>[],
  outletOptions: string[],
  categoryOptions: string[],
  productType?: ProductEnum | string | null,
  existingItems: Record<string, unknown>[] = [],
): RetailItemImportValidationResult => {
  const rowErrors: string[] = [];
  const seenItemNumbers = new Set<string>();
  const seenItemDescriptions = new Set<string>();

  const existingItemNumbers = new Set(
    existingItems
      .map((item) =>
        normalizeKey(item[RetailItemExcelSourceField.ItemNumber]),
      )
      .filter(Boolean),
  );
  const existingItemDescriptions = new Set(
    existingItems
      .map((item) =>
        normalizeKey(item[RetailItemExcelSourceField.ItemDescription]),
      )
      .filter(Boolean),
  );

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const itemNumber = String(
      row[RetailItemExcelSourceField.ItemNumber] ?? "",
    ).trim();
    const itemNumberKey = normalizeKey(itemNumber);
    if (itemNumberKey) {
      if (seenItemNumbers.has(itemNumberKey)) {
        rowErrors.push(
          `Row ${rowNumber}: Item Number "${itemNumber}" is duplicated in the file.`,
        );
      } else if (existingItemNumbers.has(itemNumberKey)) {
        rowErrors.push(
          `Row ${rowNumber}: Item Number "${itemNumber}" already exists in Retail Items Setup.`,
        );
      } else {
        seenItemNumbers.add(itemNumberKey);
      }
    }

    const itemDescription = String(
      row[RetailItemExcelSourceField.ItemDescription] ?? "",
    ).trim();
    const itemDescriptionKey = normalizeKey(itemDescription);
    if (itemDescriptionKey) {
      if (seenItemDescriptions.has(itemDescriptionKey)) {
        rowErrors.push(
          `Row ${rowNumber}: Item Description "${itemDescription}" is duplicated in the file.`,
        );
      } else if (existingItemDescriptions.has(itemDescriptionKey)) {
        rowErrors.push(
          `Row ${rowNumber}: Item Description "${itemDescription}" already exists in Retail Items Setup.`,
        );
      } else {
        seenItemDescriptions.add(itemDescriptionKey);
      }
    }

    const outletCode = row[RetailItemExcelSourceField.OutletCode];
    if (!isAllowedAutocompleteValue(outletCode, outletOptions)) {
      rowErrors.push(
        `Row ${rowNumber}: Outlet Code "${String(outletCode ?? "").trim()}" is not defined in Outlet Setup.`,
      );
    }

    const category = row[RetailItemExcelSourceField.Category];
    if (!isAllowedAutocompleteValue(category, categoryOptions)) {
      rowErrors.push(
        `Row ${rowNumber}: Category "${String(category ?? "").trim()}" is not defined in Retail Categories.`,
      );
    }

    const itemType = row[RetailItemExcelSourceField.ItemType];
    if (!isAllowedItemType(itemType, productType)) {
      rowErrors.push(
        `Row ${rowNumber}: Item Type "${String(itemType ?? "").trim()}" is not valid for this product.`,
      );
    }
  });

  if (rowErrors.length > 0) {
    return {
      valid: false,
      message:
        "Import rejected. Fix duplicates and ensure outlet, category, and item type match existing onboarding data.",
      rowErrors,
    };
  }

  return { valid: true, rowErrors: [] };
};

/** Normalizes Item Type to the numeric code stored by the Retail Items Setup form. */
export const normalizeImportedRetailItemRows = (
  rows: Record<string, unknown>[],
  productType?: ProductEnum | string | null,
  startingId = 1,
): Record<string, unknown>[] =>
  rows.map((row, index) => {
    const itemType = row[RetailItemExcelSourceField.ItemType] as
      | string
      | number
      | null
      | undefined;
    const numericType =
      getRetailItemTypeValue(
        getRetailItemTypeLabel(itemType, productType) ?? String(itemType ?? ""),
        productType,
      ) ??
      (typeof itemType === "number" ? itemType : Number(itemType));

    return {
      ...row,
      id: startingId + index,
      [RetailItemExcelSourceField.ItemType]: Number.isNaN(Number(numericType))
        ? itemType
        : Number(numericType),
    };
  });
